import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import {
  BrowserProvider,
  Contract,
  JsonRpcProvider,
  formatEther,
  parseEther,
} from "ethers";
import {
  useAppKit,
  useAppKitAccount,
  useAppKitNetwork,
  useAppKitProvider,
} from "@reown/appkit/react";
import type { Provider } from "@reown/appkit/react";
import { bsc } from "@reown/appkit/networks";

import { donationVaultAbi } from "../abi/donationVaultAbi";
import {
  BSC_CHAIN_ID,
  BSC_RPC_URL,
  DONATION_CONTRACT,
  shortAddress,
} from "../config/donation";
import { appKitModal } from "../config/appkit";

type Donation = {
  donor: string;
  amount: bigint;
  name: string;
  note: string;
  timestamp: bigint;
};

type DonationContract = Contract & {
  totalRaised: () => Promise<bigint>;
  donationCount: () => Promise<bigint>;
  getRecentDonations: (limit: number) => Promise<Donation[]>;
  donate: (name: string, note: string, options: { value: bigint }) => Promise<any>;
};

type BnbPrice = {
  usd: number;
  eur: number;
  loading: boolean;
};

const readProvider = new JsonRpcProvider(BSC_RPC_URL);

const DONATION_GOAL_EUR = Number(import.meta.env.VITE_DONATION_GOAL_EUR || "100");

// preserved from your pasted file
const AMOUNTS = ["0.0025", "0.005", "0.01", "0.02"];

function getReadContract() {
  return new Contract(
    DONATION_CONTRACT,
    donationVaultAbi,
    readProvider
  ) as unknown as DonationContract;
}

async function fetchBnbPrice(): Promise<{ usd: number; eur: number }> {
  const res = await fetch(
    "https://api.coingecko.com/api/v3/simple/price?ids=binancecoin&vs_currencies=usd,eur"
  );

  if (!res.ok) {
    throw new Error("Failed to fetch BNB price.");
  }

  const data = await res.json();

  return {
    usd: Number(data?.binancecoin?.usd || 0),
    eur: Number(data?.binancecoin?.eur || 0),
  };
}

function asNumber(value: string) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatBnb(value: bigint | string | number, maxDigits = 6) {
  try {
    const raw = typeof value === "bigint" ? value : BigInt(value);

    return Number(formatEther(raw)).toLocaleString(undefined, {
      maximumFractionDigits: maxDigits,
    });
  } catch {
    return "0";
  }
}

function formatFiat(value: number, currency: "USD" | "EUR") {
  if (!Number.isFinite(value)) value = 0;

  return value.toLocaleString(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  });
}

function donationDate(timestamp: bigint) {
  const ms = Number(timestamp) * 1000;
  if (!ms) return "";
  return new Date(ms).toLocaleDateString();
}

export default function DonatePage() {
  const { open } = useAppKit();
  const { address, isConnected } = useAppKitAccount();
  const { chainId } = useAppKitNetwork();
  const { walletProvider } = useAppKitProvider<Provider>("eip155");

  const [totalRaised, setTotalRaised] = useState<bigint>(0n);
  const [donationCount, setDonationCount] = useState<bigint>(0n);
  const [recentDonations, setRecentDonations] = useState<Donation[]>([]);

  // preserved from your pasted file
  const [amount, setAmount] = useState("0.0025");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");

  const [loadingData, setLoadingData] = useState(false);
  const [sendingDonation, setSendingDonation] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  const [bnbPrice, setBnbPrice] = useState<BnbPrice>({
    usd: 0,
    eur: 0,
    loading: false,
  });

  const wrongNetwork = isConnected && Number(chainId) !== BSC_CHAIN_ID;
  const amountNumber = asNumber(amount);

  const totalRaisedBnb = Number(formatEther(totalRaised));
  const totalRaisedEur = totalRaisedBnb * bnbPrice.eur;
  const totalRaisedUsd = totalRaisedBnb * bnbPrice.usd;

  const progress =
    DONATION_GOAL_EUR > 0
      ? Math.min(100, (totalRaisedEur / DONATION_GOAL_EUR) * 100)
      : 0;

  const donationUsd = amountNumber * bnbPrice.usd;
  const donationEur = amountNumber * bnbPrice.eur;

  const canDonate = useMemo(() => {
    return (
      isConnected &&
      !!walletProvider &&
      !wrongNetwork &&
      !sendingDonation &&
      amountNumber > 0 &&
      name.length <= 64 &&
      note.length <= 280
    );
  }, [
    isConnected,
    walletProvider,
    wrongNetwork,
    sendingDonation,
    amountNumber,
    name.length,
    note.length,
  ]);

  const loadDonationData = useCallback(async () => {
    try {
      setLoadingData(true);

      const contract = getReadContract();

      const [nextTotalRaised, nextDonationCount, nextRecent] = await Promise.all([
        contract.totalRaised(),
        contract.donationCount(),
        contract.getRecentDonations(8),
      ]);

      setTotalRaised(nextTotalRaised);
      setDonationCount(nextDonationCount);
      setRecentDonations(nextRecent);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to load donation data.");
    } finally {
      setLoadingData(false);
    }
  }, []);

  const loadBnbPrice = useCallback(async () => {
    try {
      setBnbPrice((prev) => ({
        ...prev,
        loading: true,
      }));

      const price = await fetchBnbPrice();

      setBnbPrice({
        usd: price.usd,
        eur: price.eur,
        loading: false,
      });
    } catch (err) {
      console.error(err);

      setBnbPrice((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  }, []);

  async function connectWallet() {
    try {
      setError("");
      setStatus("");
      await open({ view: "Connect", namespace: "eip155" });
    } catch (err: any) {
      setError(err?.message || "Wallet connection failed.");
    }
  }

  async function openAccount() {
    try {
      await open({ view: "Account" });
    } catch {
      await open({ view: "Connect", namespace: "eip155" });
    }
  }

  async function switchToBsc() {
    try {
      setError("");
      setStatus("Switching to BNB Smart Chain...");
      await appKitModal.switchNetwork(bsc);
      setStatus("");
    } catch (err: any) {
      setError(err?.message || "Could not switch network.");
      setStatus("");
    }
  }

  async function donate() {
    try {
      setError("");
      setStatus("");

      if (!walletProvider) {
        await connectWallet();
        return;
      }

      if (wrongNetwork) {
        await switchToBsc();
        return;
      }

      if (amountNumber <= 0) {
        setError("Enter a valid BNB amount.");
        return;
      }

      setSendingDonation(true);
      setStatus("Confirm the donation in your wallet...");

      const ethersProvider = new BrowserProvider(walletProvider);
      const signer = await ethersProvider.getSigner();

      const contract = new Contract(
        DONATION_CONTRACT,
        donationVaultAbi,
        signer
      ) as unknown as DonationContract;

      const tx = await contract.donate(name.trim(), note.trim(), {
        value: parseEther(amount),
      });

      setStatus("Donation sent. Waiting for confirmation...");
      await tx.wait();

      setStatus("Donation confirmed. Thank you.");

      // preserved from your pasted file
      setAmount("0.0025");

      setName("");
      setNote("");

      await loadDonationData();
    } catch (err: any) {
      console.error(err);
      setError(
        err?.shortMessage ||
          err?.reason ||
          err?.message ||
          "Donation failed."
      );
      setStatus("");
    } finally {
      setSendingDonation(false);
    }
  }

  function primaryAction() {
    if (!isConnected) {
      connectWallet();
      return;
    }

    if (wrongNetwork) {
      switchToBsc();
      return;
    }

    donate();
  }

  function primaryActionLabel() {
    if (!isConnected) return "Connect wallet";
    if (wrongNetwork) return "Switch to BSC";
    if (sendingDonation) return "Processing...";
    return `Donate ${amount || "0"} BNB`;
  }

  function primaryActionIcon() {
    if (sendingDonation) return <CircularProgress size={18} />;
    if (!isConnected) return <AccountBalanceWalletRoundedIcon />;
    if (wrongNetwork) return <WarningAmberRoundedIcon />;
    return <VolunteerActivismRoundedIcon />;
  }

  useEffect(() => {
    loadDonationData();
    loadBnbPrice();
  }, [loadDonationData, loadBnbPrice]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#012501",
        backgroundImage: "url('/3d-forest.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        color: "#000",
        py: { xs: 0, sm: 3 },
      }}
    >
      <Container
          maxWidth="sm"
          disableGutters
          sx={{
            px: { xs: 0, sm: 2 },
          }}
        >
        <Box>
            <Chip
              icon={<FavoriteRoundedIcon />}
              label="Help for a friend in need"
              size="small"
              sx={{
                bgcolor: "rgba(255,255,255,0.08)",
                color: "#000",
                fontWeight: 900,
                borderRadius: 1,
                mb: 1.2,
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: 30, sm: 40 },
                lineHeight: 1,
                fontWeight: 950,
                letterSpacing: "-0.04em",
              }}
            >
              <p style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <img src="/foxyswaplogoblack.png" alt="Foxyswap Logo" width="60%" />
              </p>
              For a Friend in Need
            </Typography>

            <Typography
              sx={{
                color: "rgba(0, 0, 0, 1.0",
                mt: 0.8,
              }}
            >
              <p>We are trying to help a developer friend who is having some health and financial issues, so I am asking you, as a community; it is time to come together, for the price of a morning coffee, please make a small donation to help a friend in need, and you can make a big impact on someone's life, just showing we care!</p>
            </Typography>
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 1.0)",
                mt: 0.8,
              }}
            >
            <p>Have a great day, and thank you for your support.</p>
            </Typography>
        </Box>
      </Container>

      <Container
        maxWidth="sm"
        disableGutters
        sx={{
          px: { xs: 0, sm: 2 },
        }}
      >
        <Card
          sx={{
            bgcolor: "#12161c",
            color: "#fff",
            borderRadius: { xs: 0, sm: 2 },
            border: { xs: "0", sm: "1px solid rgba(255,255,255,0.1)" },
            boxShadow: { xs: "none", sm: "0 18px 60px rgba(0,0,0,0.32)" },
            minHeight: { xs: "100vh", sm: "auto" },
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={2.4}>
              <Box>
                <Chip
                  icon={<FavoriteRoundedIcon />}
                  label="Help a friend in need"
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.08)",
                    color: "#fff",
                    fontWeight: 900,
                    borderRadius: 1,
                    mb: 1.2,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: { xs: 30, sm: 40 },
                    lineHeight: 1,
                    fontWeight: 950,
                    letterSpacing: "-0.04em",
                  }}
                >
                  Make a donation
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.72)",
                    mt: 0.8,
                  }}
                >
                  Pick an amount, add optional details, then confirm in your wallet.
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" variant="filled" onClose={() => setError("")}>
                  {error}
                </Alert>
              )}

              {status && (
                <Alert severity="info" variant="filled" onClose={() => setStatus("")}>
                  {status}
                </Alert>
              )}

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.2,
                  bgcolor: "#0d1117",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <Stack spacing={1.3}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.64)",
                          fontSize: 13,
                        }}
                      >
                        Raised
                      </Typography>

                      <Typography
                        sx={{
                          fontWeight: 950,
                          fontSize: 24,
                        }}
                      >
                        {formatFiat(totalRaisedEur, "EUR")}
                      </Typography>

                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.58)",
                          fontSize: 13,
                        }}
                      >
                        {formatBnb(totalRaised)} BNB · {formatFiat(totalRaisedUsd, "USD")}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.64)",
                          fontSize: 13,
                        }}
                      >
                        Goal
                      </Typography>

                      <Typography
                        sx={{
                          fontWeight: 950,
                          fontSize: 24,
                        }}
                      >
                        {formatFiat(DONATION_GOAL_EUR, "EUR")}
                      </Typography>

                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.58)",
                          fontSize: 13,
                        }}
                      >
                        {donationCount.toString()} donations
                      </Typography>
                    </Box>
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 9,
                      borderRadius: 1,
                      bgcolor: "rgba(255,255,255,0.08)",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: "#fff",
                        borderRadius: 1,
                      },
                    }}
                  />

                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.64)",
                      fontSize: 13,
                    }}
                  >
                    {Math.round(progress)}% funded
                    {bnbPrice.eur > 0
                      ? ` · BNB ${formatFiat(bnbPrice.eur, "EUR")}`
                      : ""}
                  </Typography>
                </Stack>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontWeight: 900,
                    mb: 1,
                  }}
                >
                  Select amount
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 1,
                  }}
                >
                  {AMOUNTS.map((item) => {
                    const active = amount === item;

                    return (
                      <Button
                        key={item}
                        fullWidth
                        onClick={() => setAmount(item)}
                        sx={{
                          minHeight: 52,
                          borderRadius: 1.2,
                          bgcolor: active ? "#fff" : "#1c222b",
                          color: active ? "#111" : "#fff",
                          border: active
                            ? "1px solid #fff"
                            : "1px solid rgba(255,255,255,0.11)",
                          fontWeight: 950,
                          "&:hover": {
                            bgcolor: active ? "#fff" : "#242b35",
                          },
                        }}
                      >
                        {item} BNB
                      </Button>
                    );
                  })}
                </Box>

                {amountNumber > 0 && donationEur > 0 && (
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.68)",
                      fontSize: 14,
                      mt: 1,
                    }}
                  >
                    Estimated value: {formatFiat(donationEur, "EUR")} ·{" "}
                    {formatFiat(donationUsd, "USD")}
                  </Typography>
                )}
              </Box>

              <Stack spacing={1.4}>
              <TextField
                label="Custom amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.0025"
                type="number"
                slotProps={{
                  htmlInput: {
                    min: "0",
                    step: "0.0001",
                  },
                  input: {
                    endAdornment: <InputAdornment position="end">BNB</InputAdornment>,
                  },
                }}
                fullWidth
              />

                <TextField
                  label="Name, optional"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Anonymous"
                  slotProps={{
                    htmlInput: {
                      maxLength: 64,
                    },
                  }}
                  fullWidth
                />

                <TextField
                  label="Note, optional"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Sending support."
                  slotProps={{
                    htmlInput: {
                      maxLength: 280,
                    },
                  }}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Stack>

              <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.2 }}>
                Name and note are public on-chain. Leave both blank for privacy.
              </Alert>

              {wrongNetwork && (
                <Alert severity="warning" sx={{ borderRadius: 1.2 }}>
                  Your wallet is not on BNB Smart Chain.
                </Alert>
              )}

              <Button
                size="large"
                variant="contained"
                fullWidth
                onClick={primaryAction}
                disabled={sendingDonation || (isConnected && !wrongNetwork && !canDonate)}
                startIcon={primaryActionIcon()}
                sx={{ minHeight: 54, px: 3 }}
              >
                {primaryActionLabel()}
              </Button>

              {isConnected && (
                <Button
                  size="large"
                  variant="outlined"
                  color="inherit"
                  fullWidth
                  onClick={openAccount}
                  sx={{
                    minHeight: 50,
                    borderColor: "rgba(255,255,255,0.2)",
                  }}
                >
                  Wallet: {shortAddress(address)}
                </Button>
              )}
            </Stack>
          </CardContent>

          <Divider />

          <CardContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#10141a" }}>
            <Stack spacing={1.4}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 950,
                    fontSize: 20,
                  }}
                >
                  Supporters
                </Typography>

                <Button
                  variant="text"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    loadDonationData();
                    loadBnbPrice();
                  }}
                  startIcon={
                    loadingData || bnbPrice.loading ? (
                      <CircularProgress size={14} />
                    ) : (
                      <RefreshRoundedIcon />
                    )
                  }
                >
                  Refresh
                </Button>
              </Box>

              {loadingData ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 3,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : recentDonations.length === 0 ? (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1.2,
                    bgcolor: "#0d1117",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <Typography sx={{ fontWeight: 900 }}>No supporters yet</Typography>

                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.68)",
                    }}
                  >
                    Donations will appear here after they confirm on-chain.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {recentDonations.map((donation, index) => {
                    const displayName = donation.name?.trim() || "Anonymous";
                    const displayNote = donation.note?.trim();

                    return (
                      <Box
                        key={`${donation.donor}-${donation.timestamp.toString()}-${index}`}
                        sx={{
                          p: 1.5,
                          borderRadius: 1.2,
                          bgcolor: "#0d1117",
                          border: "1px solid rgba(255,255,255,0.08)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 2,
                            alignItems: "flex-start",
                          }}
                        >
                          <Box>
                            <Typography sx={{ fontWeight: 950 }}>
                              {displayName}
                            </Typography>

                            <Typography
                              sx={{
                                color: "rgba(255,255,255,0.58)",
                                fontSize: 13,
                              }}
                            >
                              {donationDate(donation.timestamp)}
                            </Typography>
                          </Box>

                          <Typography sx={{ fontWeight: 950 }}>
                            {formatBnb(donation.amount, 5)} BNB
                          </Typography>
                        </Box>

                        {displayNote && (
                          <Typography
                            sx={{
                              mt: 1,
                              color: "rgba(255,255,255,0.78)",
                            }}
                          >
                            {displayNote}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
